import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeft } from '@peacecover/icon';
import { useHandleFanStyleStep } from '../hooks/useHandleFanStyleStep';
import SpeechBalloon from '../../chat/components/SpeechBallon';
import FanStyleProgressBar from './FanStyleProgressBar';
import FanStyleOptionCard from './FanStyleOptionCard';
import GradientMageCircle from './GradientMageCircle';
import { FAN_STYLE_OPTIONS } from '../constants/fanStyle';
import type { FanBuddySettingStep, FanStyle } from '../FanBuddySettingPage';

interface ChooseFanStyleStepProps {
  handleSettingStep: (step: FanBuddySettingStep) => void;
  onUpdateFanStyle: (fanStyle: Partial<FanStyle>) => void;
  initialStep?: keyof typeof FAN_STYLE_OPTIONS;
}

const ChooseFanStyleStep = ({
  handleSettingStep,
  onUpdateFanStyle,
  initialStep
}: ChooseFanStyleStepProps) => {
  const { t } = useTranslation();
  const { currentStep, handleOptionClick, handlePreviousStep } = useHandleFanStyleStep({
    onUpdateFanStyle: onUpdateFanStyle,
    onComplete: () => handleSettingStep('select-buddy-keywords'),
    initialStep
  });

  return (
    <div className="min-h-screen pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <Header handlePreviousStep={handlePreviousStep} />
      <div className="px-[24px]">
        <h1 className="text-[26px] font-bold leading-[31px] text-text-main-lv1 my-[10px] whitespace-pre-wrap">
          {t('FanBuddySetting.fanStyleTitle')}
        </h1>
        <FanStyleStepContent
          key={currentStep}
          currentStep={currentStep}
          handleOptionClick={handleOptionClick}
        />
      </div>
    </div>
  );
};

export default ChooseFanStyleStep;

const FanStyleStepContent = ({
  currentStep,
  handleOptionClick
}: {
  currentStep: keyof typeof FAN_STYLE_OPTIONS;
  handleOptionClick: (optionId: string) => void;
}) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  const handleTypingComplete = useCallback(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="flex flex-col items-center">
      <SpeechBalloon
        text={t(`FanBuddySetting.questions.${currentStep}`)}
        className="mt-[16px]"
        containerClassName="rounded-[30px]"
        onTypingComplete={handleTypingComplete}
      />
      <GradientMageCircle />
      <FanStyleProgressBar currentStep={currentStep} />
      <AnimatePresence>
        {isVisible && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="w-full"
          >
            <div className="mt-[28px] flex flex-col gap-[10px] w-full">
              {FAN_STYLE_OPTIONS[currentStep].map((option) => (
                <FanStyleOptionCard
                  key={option}
                  option={option}
                  handleOptionClick={handleOptionClick}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Header = ({ handlePreviousStep }: { handlePreviousStep: () => void }) => {
  const { t } = useTranslation();
  return (
    <header className="px-[16px] py-[12px] flex items-center justify-center relative w-full">
      <button
        type="button"
        onClick={handlePreviousStep}
        className="absolute left-[16px] top-[50%] translate-y-[-50%]"
      >
        <ArrowLeft size={24} className="text-text-main-lv1" />
      </button>
      <p className="text-text-main-lv1 text-[17px] font-bold leading-[20px]">
        {t('FanBuddySetting.title')}
      </p>
    </header>
  );
};
